import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { TabType } from "../../../services/helpers/types";
import { useDispatch } from "react-redux";

import { setTabsPositions } from "../../../services/edit/tabsEdit.service";
import DraggableTabsItem from "../DraggableTabsItem/DraggableTabsItem";
import { SelectedTabType } from "../../molecules/Tabs/Tabs";
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { deleteTab } from "../../../services/api/tabs.api";
import { setIsEditDrawerOpen } from "../../../redux/reducers/appReducer";

interface DraggableTabsListProps {
  tabs: TabType[];
  currentTab?: SelectedTabType;
  onTabChange: (itemID: number, index: number, forceRefresh?: boolean) => void;
  onPositionChange: (index: number, itemID: number, newTabs: TabType[]) => void;
  onDeleteTab: (itemID: number) => void;
  onTabUpdate: (
    tabID: number,
    name: string,
    value: string,
    position: number
  ) => void;
}

const DraggableTabsList = (props: DraggableTabsListProps) => {
  const {
    tabs,
    currentTab,
    onTabChange,
    onPositionChange,
    onDeleteTab,
    onTabUpdate,
  } = props;

  const dispatch = useDispatch();

  const [items, setItems] = useState<number[]>([]);

  useEffect(() => {
    const tabsIds = tabs.map((tab) => tab.tabID);
    setItems(tabsIds);
  }, [tabs]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor)
  );

  const handleChangePosition = (newItems: number[]) => {
    const newTabs: TabType[] = newItems.map((item, index) => {
      const newItem = tabs.find((tab) => tab.tabID === item);
      if (!newItem) {
        return tabs[index];
      }
      return { ...newItem, position: index + 1 };
    });
    setTabsPositions(dispatch, newTabs);
  };

  const updatedItems = (items: number[], active: any, over: any) => {
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);

    return arrayMove(items, oldIndex, newIndex);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const newItems = updatedItems(items, active, over);

      const newIndex = newItems.indexOf(currentTab?.tabID ?? 0);

      const newTabs = newItems.map((item, index) => {
        const newItem = tabs.find((tab) => tab.tabID === item);
        if (!newItem) {
          return tabs[index];
        }
        return { ...newItem, position: index + 1 };
      });

      onPositionChange(newIndex, active?.id, newTabs);

      setItems(newItems);
      handleChangePosition(newItems);
    }
  };

  const handleDelete = (itemID: number) => {
    dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }));
    setItems(items.filter((id) => id !== itemID));
    deleteTab(dispatch, itemID).then((response) => {
      if (currentTab?.tabID === itemID) {
        if (items.length > 0) {
          const newItemID = items[0] === itemID ? items[1] : items[0];
          onTabChange(newItemID, 0, true);
        }
      }
      onDeleteTab(itemID);
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
    >
      <SortableContext items={items} strategy={horizontalListSortingStrategy}>
        {items.map((id, index) => (
          <DraggableTabsItem
            key={id}
            id={id}
            tab={tabs.find((item) => item.tabID === id)}
            currentTab={currentTab?.tabID}
            onTabChange={(itemID) => {
              dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }));

              onTabChange(itemID, index);
            }}
            onDelete={handleDelete}
            onTabUpdate={onTabUpdate}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default DraggableTabsList;
